// Editorial content for the offer-category landing pages (/offers/<type>).
//
// WHY THIS FILE EXISTS
//   A category page whose only content is a grid of offer cards is, to Google,
//   a near-duplicate of /offers with a different filter applied — exactly the
//   thin-aggregation pattern that got this domain's offer pages ignored in the
//   first place (see src/utils/indexable.js). The intro and highlights below
//   are what make each hub a distinct, rankable page rather than a filter view.
//
//   Keep the prose specific to the category. Generic "find the best deals
//   here" filler defeats the point — it reads as boilerplate to a crawler
//   precisely because it would be true of every other category too.
//
// Keyed by the DISPLAY name in src/constants/categories.js (OFFER_CATEGORIES),
// which is also what the API's `category` query param expects.

// The API category is 'Card Offer', so naively appending our own noun gives
// "Card Offer Offers". Strip a trailing Offer/Offers before appending.
// Display only — the API `category` param must keep the exact category name.
export function displayLabel(categoryName = '') {
  return categoryName.replace(/\s*offers?$/i, '').trim() || categoryName;
}

export const CATEGORY_META = {
  Shopping: {
    color: '#6366F1',
    accent: '#8B5CF6',
    tagline: 'Everyday essentials, home, kitchen & general merchandise deals',
    intro:
      'General shopping offers spanning home and kitchen, storage and organisers, decor, cleaning supplies and everyday essentials across Flipkart, Amazon, Myntra and Ajio. This is the widest category on OfferLelo, so it is where the bulk-buy packs, combo offers and under-₹500 finds usually turn up first.',
    highlights: [
      'Home, kitchen & storage deals under ₹500',
      'Combo packs and multi-buy offers',
      'Decor, furnishings & organiser discounts',
      'Daily essentials from Flipkart, Amazon, Myntra & Ajio',
    ],
    keywords:
      'online shopping offers india, home kitchen deals, storage organiser offers, flipkart shopping deals, amazon household offers, cheap online shopping india',
  },

  Electronics: {
    color: '#0EA5E9',
    accent: '#2563EB',
    tagline: 'Mobiles, audio, appliances & gadget discounts',
    intro:
      'Electronics offers covering smartphones, headphones and earbuds, smartwatches, kitchen appliances, fans, coolers and TVs. Electronics deals move fastest during Big Billion Days and the Great Indian Festival, and this is also the category where bank instant discounts and no-cost EMI make the biggest difference to the final price you pay.',
    highlights: [
      'Smartphone launch offers with bank cashback',
      'Headphones, earbuds & smartwatch discounts',
      'Kitchen appliances, fans, coolers & air conditioners',
      'No-cost EMI and exchange offers on big-ticket items',
    ],
    keywords:
      'electronics offers india, mobile offers today, headphone deals, smartwatch offers, appliance discount india, no cost emi electronics, flipkart electronics sale',
  },

  Fashion: {
    color: '#FF3F6C',
    accent: '#DB2777',
    tagline: 'Clothing, footwear, watches & accessories offers',
    intro:
      'Fashion offers across mens and womens clothing, footwear, watches, bags and accessories, drawn mainly from Myntra, Ajio and Flipkart. Discounts here run deeper than any other category — end-of-season clearances routinely cross 70% off — so it pays to check sizes and return windows before buying rather than after.',
    highlights: [
      'Myntra End of Reason Sale & Ajio Big Bold Sale deals',
      'Mens and womens clothing at 50–80% off',
      'Footwear, sneakers & sandals discounts',
      'Watches, bags, jewellery & accessory offers',
    ],
    keywords:
      'fashion offers india, myntra offers today, ajio sale, clothing deals india, footwear discount, mens fashion sale, womens clothing offers',
  },

  Food: {
    color: '#F59E0B',
    accent: '#EA580C',
    tagline: 'Grocery, snacks, beverages & food delivery offers',
    intro:
      'Food and grocery offers covering packaged staples, snacks, beverages, health foods and supplements, plus food-delivery and restaurant promotions. Grocery discounts tend to be smaller in percentage terms than electronics or fashion but repeat monthly, so subscribe-and-save and bulk-pack offers usually beat a one-off flash deal here.',
    highlights: [
      'Grocery staples & bulk pack offers',
      'Snacks, beverages & breakfast deals',
      'Health foods, protein & supplement discounts',
      'Food delivery and restaurant promo codes',
    ],
    keywords:
      'grocery offers india, food deals today, snacks discount online, beverage offers, supplement deals india, food delivery coupon',
  },

  'Card Offer': {
    color: '#16A34A',
    accent: '#0D9488',
    tagline: 'Credit card, debit card & bank cashback offers',
    intro:
      'Bank and card offers that stack on top of a seller discount — instant discounts, cashback, reward-point multipliers, no-cost EMI and welcome benefits from HDFC, SBI, ICICI, Axis and Kotak. These are the offers most shoppers leave unclaimed, because the discount only appears at the payment step and only for specific card and minimum-order combinations.',
    highlights: [
      'Instant discounts on HDFC, SBI, ICICI, Axis & Kotak cards',
      'Cashback and reward-point multiplier offers',
      'No-cost EMI eligibility across stores',
      'New-card welcome benefits and milestone rewards',
    ],
    keywords:
      'credit card offers india, bank cashback offer, hdfc card discount, sbi card offer, icici bank offer, no cost emi card, debit card cashback',
  },

  Travel: {
    color: '#0891B2',
    accent: '#0E7490',
    tagline: 'Flight, hotel & holiday booking offers',
    intro:
      'Travel offers on domestic and international flights, hotel bookings, holiday packages and bus and train travel. Travel pricing changes by the hour and is highly seasonal, so the discounts listed here are worth checking against the booking site directly before you commit — a percentage-off coupon is often beaten by an off-peak fare.',
    highlights: [
      'Domestic & international flight booking discounts',
      'Hotel and homestay offers with bank cashback',
      'Holiday and honeymoon package deals',
      'Bus & train booking promo codes',
    ],
    keywords:
      'travel offers india, flight booking discount, hotel booking offer, holiday package deals india, cheap flight coupon, bus booking offer',
  },

  Others: {
    color: '#64748B',
    accent: '#475569',
    tagline: 'Deals that do not fit the main categories',
    intro:
      'Offers that sit outside the main categories — services, subscriptions, gift cards, recharge and bill-payment promotions, and one-off deals that do not belong under shopping, fashion, electronics, food, travel or card offers. This category is deliberately a catch-all, so it is worth browsing rather than searching.',
    highlights: [
      'Subscription and streaming service offers',
      'Gift card and voucher discounts',
      'Recharge & bill payment cashback',
      'Miscellaneous one-off deals',
    ],
    keywords:
      'other offers india, subscription deals, gift card discount india, recharge cashback offer, miscellaneous deals',
  },
};
